/*
 * hyperion-index:begin
 * ---
 * path: hyperion-fe/src/platform/auth/AuthProvider.tsx
 * layer: web
 * area: providers
 * kind: source
 * web_root: providers
 * relpath: web/src/providers/AuthProvider.tsx
 * filename: AuthProvider.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import { createContext, useEffect, useState, useCallback, PropsWithChildren } from 'react';
import axios from 'axios';
import { z } from 'zod';
import { api, unwrapOne } from '@/platform/api-client/client';
import type { User } from '@/platform/api-client/types';
import { identifyUser, resetPostHog } from '@/platform/observability/PostHogProvider';
import { consumeReturnTo, locationToPath, rememberReturnTo } from './returnTo';
import { hasSkippedPin } from './pinSkip';

/** Second-factor ids, matching the backend's /auth/me `mfa.methods`. */
export type EnrolledMfaMethod = 'totp' | 'webauthn' | 'sms' | 'backup';
const ENROLLED_MFA_METHODS: readonly EnrolledMfaMethod[] = ['totp', 'webauthn', 'sms', 'backup'];

const userSchema: z.ZodType<User> = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: z.string(),
  roles: z.array(z.string()),
  permissions: z.array(z.string()).optional(),
  routeExclusions: z.array(z.string()).optional(),
  tenantId: z.string(),
  avatarUrl: z.string().optional(),
});
const authMeSchema = z.object({
  user: userSchema,
  mfa: z
    .object({
      enabled: z.boolean(),
      verified: z.boolean(),
      methods: z.array(z.string()).optional(),
    })
    .optional(),
  pin: z.object({ set: z.boolean() }).optional(),
});
const refreshSessionSchema = z.object({ user: userSchema.optional() });
const httpErrorShapeSchema = z.object({
  response: z.object({ status: z.number().int(), data: z.unknown().optional() }),
});

function httpErrorShape(error: unknown) {
  if (axios.isAxiosError(error)) {
    return { status: error.response?.status, data: error.response?.data };
  }
  const parsed = httpErrorShapeSchema.safeParse(error);
  return parsed.success ? parsed.data.response : undefined;
}

export interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** @deprecated HttpOnly cookies handle auth. Use isAuthenticated instead. Kept for backward compat. */
  token: string | null;
  mfaRequired: boolean;
  mfaEnroll: boolean;
  pinRequired: boolean;
  /**
   * Second factors the user has completed enrolment for, in the order the
   * challenge should offer them. Undefined when the server did not say
   * (older backend or lookup failure) — callers must then offer every method
   * rather than guess, so nobody is locked out of a factor they own.
   */
  mfaMethods?: EnrolledMfaMethod[];
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  /** @deprecated No-op with HttpOnly cookies. Kept for backward compat (e.g. TenantProvider). */
  setToken: (token: string | null) => void;
  updateUser: (user: User) => void;
  clearMfaState: () => void;
  clearPinRequired: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

type MfaGate = 'none' | 'challenge' | 'enroll';

/**
 * Resolve the server's MFA state into the one authentication surface that may
 * be entered. An unverified session is not app-ready: existing factors go to
 * the challenge, while users without a factor must complete enrolment.
 */
export function resolveMfaGate(mfa: { enabled: boolean; verified: boolean } | undefined): MfaGate {
  if (!mfa || mfa.verified) return 'none';
  return mfa.enabled ? 'challenge' : 'enroll';
}

/** Keep only method ids the client understands, preserving server order. */
export function parseEnrolledMethods(methods: readonly string[] | undefined): EnrolledMfaMethod[] | undefined {
  if (!methods) return undefined;
  const known = methods.filter((m): m is EnrolledMfaMethod =>
    (ENROLLED_MFA_METHODS as readonly string[]).includes(m)
  );
  return known.length > 0 ? known : undefined;
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaEnroll, setMfaEnroll] = useState(false);
  const [pinRequired, setPinRequired] = useState(false);
  const [mfaMethods, setMfaMethods] = useState<EnrolledMfaMethod[] | undefined>(undefined);

  // Listen for session expiry events from the axios interceptor.
  // This replaces the old window.location.href = '/login' hard redirect,
  // which caused a full page reload → remount → re-check → loop.
  useEffect(() => {
    const handleSessionExpired = () => {
      setIsAuthed(false);
      setUser(null);
      setMfaRequired(false);
      setMfaEnroll(false);
      localStorage.removeItem('current_tenant_id');
      // ProtectedRoute will redirect to /login via React Router (no reload)
    };
    window.addEventListener('auth-session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth-session-expired', handleSessionExpired);
  }, []);

  // MFA failures from protected API calls are handled as React state, not a
  // hard page reload. This lets ProtectedRoute move to the challenge once
  // without remounting every provider and replaying their startup requests.
  useEffect(() => {
    const handleMfaRequired = () => {
      setMfaRequired(true);
      setMfaEnroll(false);
    };
    window.addEventListener('auth-mfa-required', handleMfaRequired);
    return () => window.removeEventListener('auth-mfa-required', handleMfaRequired);
  }, []);

  // Check for existing session on mount by calling /auth/me (cookie sent automatically)
  useEffect(() => {
    const checkAuth = async () => {
      const fetchSession = async () => {
        const response = await api.get<unknown>('/auth/me', { baseURL: '' });
        return unwrapOne(response.data, authMeSchema, 'Authenticated session');
      };

      try {
        let data: Awaited<ReturnType<typeof fetchSession>>;
        try {
          data = await fetchSession();
        } catch (error: unknown) {
          if (httpErrorShape(error)?.status === 401) {
            await api.post('/auth/refresh', {}, { baseURL: '' });
            data = await fetchSession();
          } else {
            throw error;
          }
        }

        // The /auth/me endpoint returns { user, session, mfa, pin }
        const userData = data.user;
        setUser(userData);
        setIsAuthed(true);
        identifyUser(userData);

        // Restore the complete MFA gate from /auth/me. Callback query params
        // are transient, so returning/reloaded sessions must derive the same
        // challenge-vs-enrol decision from server state.
        const mfaGate = resolveMfaGate(data.mfa);
        setMfaRequired(mfaGate === 'challenge');
        setMfaEnroll(mfaGate === 'enroll');
        setMfaMethods(parseEnrolledMethods(data.mfa?.methods));

        // Check PIN status — if MFA verified but no PIN set, prompt for PIN
        // setup, unless the user already chose "Skip for now" this session.
        if (data.mfa?.verified && data.pin && !data.pin.set && !hasSkippedPin(userData.id)) {
          setPinRequired(true);
        }
      } catch (error: unknown) {
        // Check for MFA-related 403
        const httpError = httpErrorShape(error);
        if (httpError?.status === 403) {
          const parsed = z.object({ error: z.string().optional() }).safeParse(httpError.data);
          const code = parsed.success ? parsed.data.error : undefined;
          if (code === 'mfa_required' || code === 'mfa_expired') {
            setMfaRequired(true);
            setIsAuthed(true); // We have a session, just need MFA
            setIsLoading(false);
            return;
          }
        }
        // No valid session — just set state, don't redirect (ProtectedRoute handles it)
        setIsAuthed(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = useCallback(async () => {
    // The OAuth round trip loses router state; keep the current page (when it
    // is a real destination, not an auth surface) for after sign-in.
    rememberReturnTo(locationToPath(window.location));
    // Navigate to server-side OAuth endpoint which redirects to Microsoft
    window.location.href = '/auth/login';
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', null, { baseURL: '' });
    } catch (error) {
      // 401 means the server session is already gone: that is a completed
      // logout, not an error (it was the most common prod client "error").
      // Anything else failed server-side, but local cleanup still proceeds.
      if (!(axios.isAxiosError(error) && error.response?.status === 401)) {
        console.error('Server logout failed:', error);
      }
    } finally {
      localStorage.removeItem('current_tenant_id');
      setIsAuthed(false);
      setUser(null);
      setMfaRequired(false);
      setMfaEnroll(false);
      resetPostHog();
      // Don't navigate here - let the component handle it
    }
  }, []);

  // No-op — HttpOnly cookies handle token storage. Kept for backward compat
  // (e.g. TenantProvider calls setToken after tenant switch).
  const setTokenFn = useCallback((_newToken: string | null) => {
    // With HttpOnly cookies, the backend sets the cookie directly.
    // Just re-check auth state to pick up any changes.
    if (_newToken) {
      setIsAuthed(true);
    }
  }, []);

  const refreshTokenFn = useCallback(async () => {
    try {
      // Cookie sent automatically, backend sets new cookie in response
      const response = await api.post<unknown>('/auth/refresh', {}, { baseURL: '' });
      const refreshed = unwrapOne(response.data, refreshSessionSchema, 'Refreshed session');
      setIsAuthed(true);
      if (refreshed.user) {
        setUser(refreshed.user);
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      await logout();
      throw error;
    }
  }, [logout]);

  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser);
  }, []);

  const clearMfaState = useCallback(() => {
    setMfaRequired(false);
    setMfaEnroll(false);
  }, []);

  const clearPinRequired = useCallback(() => {
    setPinRequired(false);
  }, []);

  // Handle OAuth callback — backend already set HttpOnly cookies before redirecting here.
  // We just need to check for MFA/enroll params and clean up the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const mfaRequiredParam = params.get('mfa_required') === 'true';
    const mfaEnrollParam = params.get('mfa_enroll') === 'true';
    // Detect OAuth callback by presence of callback-specific params
    const isCallback =
      params.has('mfa_required') ||
      params.has('mfa_enroll') ||
      params.has('token') ||
      params.has('error');

    if (error) {
      console.error('OAuth error:', error);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    if (isCallback) {
      setIsAuthed(true);

      // Check MFA state from callback. Auth v2 default routes:
      //   mfa_required=true → /login?step=challenge (existing user verifying)
      //   mfa_enroll=true   → /first-time           (new user enrolling)
      if (mfaRequiredParam) {
        setMfaRequired(true);
        window.history.replaceState({}, '', '/login?step=challenge');
        return;
      }
      if (mfaEnrollParam) {
        setMfaEnroll(true);
        window.history.replaceState({}, '', '/first-time');
        return;
      }

      // Clean URL and reload to fetch user (cookie is already set). Land on
      // the page the user was heading to before sign-in, when there is one.
      window.history.replaceState({}, '', consumeReturnTo('/'));
      window.location.reload();
    }
  }, []);

  return (
    <AuthContext
      value={{
        user,
        isAuthenticated: isAuthed && !!user,
        isLoading,
        token: isAuthed ? 'cookie' : null, // Compat shim — actual token is in HttpOnly cookie
        mfaRequired,
        mfaEnroll,
        pinRequired,
        mfaMethods,
        login,
        logout,
        refreshToken: refreshTokenFn,
        setToken: setTokenFn,
        updateUser,
        clearMfaState,
        clearPinRequired,
      }}
    >
      {children}
    </AuthContext>
  );
}
